import * as React from "react";
import { forwardRef, useEffect, useState } from "react";
import {
  MdClear,
  MdOutlineArrowCircleDown,
  MdOutlineArrowCircleUp,
} from "react-icons/md";
import { Popover, PopoverContent, PopoverTrigger } from "@chakra-ui/popover";
import { useDisclosure } from "@chakra-ui/hooks";
import { t } from "i18next";
import { usePermissions } from "hooks/usePermissions";
import GenericButton from "../../controls/buttons/generic";

interface InputFieldProps {
  id: string;
  value: { hour: number; minute: number } | undefined;
  onChange: (values: { hour: number; minute: number } | undefined) => void;
  debounce?: number;
  placeholder: string;
  labelKey?: string;
  valueKey?: string;
  state?: "error" | "success";
  onFocus?: () => void;
  onBlur?: () => void;
  variant?: "solid" | "outline" | "filter" | "white";
  placement?: "top" | "bottom";
  clearIcon?: boolean;
  sizes?: "lg" | "md" | "sm" | "xs";
  minWidth?: string;
  color?: "primary" | "secondary";
  rounded?: string;
  loading?: boolean;
  permissionRole?: string;
  extra?: string;
  disabled?: boolean;
  readOnly?: boolean;
  hourOnly?: boolean;
}

const TimePicker = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      value,
      id,
      debounce = 500,
      variant = "outline",
      placement = "bottom",
      sizes = "lg",
      clearIcon = true,
      placeholder,
      disabled,
      readOnly,
      onChange,
      onFocus,
      onBlur,
      state,
      minWidth = "300px",
      color = "primary",
      rounded = "rounded-2xl",
      loading = false,
      permissionRole,
      hourOnly = false,
      extra,
    },
    ref
  ) => {
    const { canShow } = usePermissions();
    const { onOpen, onClose, isOpen } = useDisclosure();
    const [displayValue, setDisplayValue] = React.useState<
      { hour: number; minute: number } | undefined
    >(undefined);
    const [selectedHour, setSelectedHour] = useState<number | null>(null);
    const [selectedMinute, setSelectedMinute] = useState<number | null>(null);
    const [pendingValue, setPendingValue] = useState<
      { hour: number; minute: number } | undefined
    >(undefined);

    useEffect(() => {
      setDisplayValue(value);
    }, [value]);

    // When popover opens, initialize selection from value
    useEffect(() => {
      if (isOpen) {
        if (value) {
          setSelectedHour(value.hour);
          setSelectedMinute(value.minute);
          setPendingValue(value);
        } else {
          setSelectedHour(null);
          setSelectedMinute(null);
          setPendingValue(undefined);
        }
      }
    }, [isOpen, value]);

    if (permissionRole && !canShow(permissionRole)) {
      return null;
    }

    const isDisabled = disabled || loading || readOnly;

    const colorClasses = {
      primary: {
        filter: `text-primary bg-gray-50`,
        solid: `text-primary bg-gray-50/10`,
        outline: `border border-primary text-primary`,
        white: `border border-secondary text-primary bg-white`,
      },
      secondary: {
        filter: `text-secondary bg-gray-50`,
        solid: `text-secondary bg-gray-50`,
        outline: `border border-secondary text-secondary bg-transparent focus:bg-white`,
        white: `border border-secondary text-primary bg-white`,
      },
    };

    const sizeClasses = {
      lg: "h-12 px-6 text-txt-lg",
      md: "h-10 px-4 text-txt-md",
      sm: "h-8 px-3 text-txt-sm",
      xs: "h-6 px-2 text-txt-xs",
    };

    const stateClasses = {
      error: "outline-red-500 text-red-500 placeholder:text-red-500",
      success: "outline-green-500 text-green-500 placeholder:text-green-500",
    };

    // Helper to format time
    const formatTime = (hour: number, minute: number) =>
      `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

    return (
      <div className={`flex-1 ${minWidth}`}>
        <Popover
          isOpen={isOpen && !disabled}
          onOpen={onOpen}
          onClose={onClose}
          closeOnBlur={true}
          matchWidth
          placement={placement}
        >
          <PopoverTrigger>
            <button
              type="button"
              id={id}
              onFocus={onFocus}
              disabled={disabled || readOnly}
              className={`flex w-full items-center justify-center  ${rounded} ${
                sizeClasses[sizes]
              } ${colorClasses[color][variant]} ${
                state ? stateClasses[state] : "outline-none"
              } ${
                isDisabled
                  ? "border-none bg-gray-50/50"
                  : "hover:opacity-50 focus:opacity-100"
              } ${extra}`}
            >
              <div
                className={`flex w-full items-center justify-between gap-2 sm:gap-4`}
              >
                <div className="flex items-center truncate">
                  {displayValue &&
                  displayValue.hour !== undefined &&
                  displayValue.minute !== undefined ? (
                    <span className={`truncate`}>
                      {formatTime(displayValue.hour, displayValue.minute)}
                    </span>
                  ) : (
                    <span className="truncate text-gray-500">
                      {placeholder}
                    </span>
                  )}
                </div>
                <div className=" flex gap-2">
                  {displayValue && clearIcon && (
                    <MdClear
                      onClick={(e) => {
                        e.stopPropagation();
                        setDisplayValue(undefined);
                        onChange(undefined);
                      }}
                      className="cursor-pointer"
                    />
                  )}
                  {isOpen ? (
                    <MdOutlineArrowCircleUp className="" />
                  ) : (
                    <MdOutlineArrowCircleDown className="" />
                  )}
                </div>
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent
            zIndex={2000}
            bg="white"
            className="flex max-h-60 w-max min-w-36 flex-col rounded-xl border px-1 py-2"
          >
            <div className="flex flex-1 gap-4 overflow-y-auto p-2">
              {/* Hours column */}
              <div className="flex flex-col items-center">
                <span className="mb-2 font-bold">Hour</span>
                <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
                  {[...Array(24).keys()].map((hour) => (
                    <button
                      key={hour}
                      className={`rounded px-3 py-1 ${
                        selectedHour === hour
                          ? "bg-background-100 text-white"
                          : "hover:bg-gray-200"
                      }`}
                      onClick={() => setSelectedHour(hour)}
                      type="button"
                    >
                      {hour.toString().padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>
              {/* Minutes column */}

              <div className="flex flex-col items-center">
                <span className="mb-2 font-bold">Minute</span>
                <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
                  {[...Array(60).keys()].map((minute) => (
                    <button
                      key={minute}
                      className={`rounded px-3 py-1 ${
                        selectedMinute === minute
                          ? "bg-background-100 text-white"
                          : "hover:bg-gray-200"
                      }`}
                      onClick={() => setSelectedMinute(minute)}
                      type="button"
                      disabled={hourOnly}
                    >
                      {minute.toString().padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 mt-0 flex justify-end gap-2 border-t bg-white px-2 pt-2">
              <GenericButton
                text={t("button.action.ok")}
                loading={selectedHour === null || selectedMinute === null}
                size="sm"
                rounded="rounded-lg"
                onClick={() => {
                  if (selectedHour !== null && selectedMinute !== null) {
                    const newValue = {
                      hour: selectedHour,
                      minute: selectedMinute,
                    };
                    setDisplayValue(newValue);
                    onChange(newValue);
                    onClose();
                  }
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

export default TimePicker;
